import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { Tables } from '../../../core/supabase/database.types';
import { RoutinesRepository } from '../domain/routines.repository';
import {
  RoutineDayEntry,
  RoutineDefinition,
  RoutineHistoryRecord,
  RoutineInput,
  RoutineStatus,
  RoutineSubtask
} from '../models/routine.model';

type RoutineOccurrenceStatus = Tables<'task_occurrences'>['status'];
type RoutineSubtaskRow = Pick<
  Tables<'task_occurrence_subtasks'>,
  'id' | 'occurrence_id' | 'title_snapshot' | 'position' | 'is_completed' | 'completed_at'
>;

@Injectable()
export class SupabaseRoutinesRepository extends RoutinesRepository {
  // Generated Supabase types require owner_id on inserts and mark nullable SQL RPC
  // parameters as non-null. The database owns those invariants, so this adapter keeps
  // the SDK client broad while its mapped rows remain explicitly typed.
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async materializeDate(date: string): Promise<void> {
    const { error } = await this.client.rpc('materialize_routine_date', { p_date: date });
    if (error) throw error;
  }

  async materializeDates(dates: string[]): Promise<void> {
    if (!dates.length) return;

    const { error } = await this.client.rpc('materialize_routine_dates', { p_dates: dates });
    if (error) throw error;
  }

  async getRoutinesForDate(date: string): Promise<RoutineDayEntry[]> {
    const { data: occurrences, error: occurrenceError } = await this.client
      .from('task_occurrences')
      .select(`
        id,
        task_definition_id,
        scheduled_for,
        status,
        cancellation_reason,
        routine_measure_type_snapshot,
        routine_measure_unit_snapshot,
        routine_default_quantity_snapshot,
        routine_quantity,
        created_at
      `)
      .eq('task_kind', 'ROUTINE')
      .eq('scheduled_for', date)
      .order('created_at', { ascending: true });

    if (occurrenceError) throw occurrenceError;
    if (!occurrences?.length) return [];

    const definitionIds = [...new Set(occurrences.map(row => row.task_definition_id))];
    const occurrenceIds = occurrences.map(row => row.id);

    const [definitionsResult, subtasksResult] = await Promise.all([
      this.client
        .from('task_definitions')
        .select(`
          id,
          title,
          description,
          priority,
          is_active,
          archived_at,
          routine_measure_type,
          routine_measure_unit,
          routine_default_quantity
        `)
        .in('id', definitionIds),
      this.client
        .from('task_occurrence_subtasks')
        .select(`
          id,
          occurrence_id,
          title_snapshot,
          position,
          is_completed,
          completed_at
        `)
        .in('occurrence_id', occurrenceIds)
        .order('position', { ascending: true })
    ]);

    if (definitionsResult.error) throw definitionsResult.error;
    if (subtasksResult.error) throw subtasksResult.error;

    const definitions = new Map(
      (definitionsResult.data ?? []).map(definition => [definition.id, definition])
    );
    const subtaskRows: RoutineSubtaskRow[] = subtasksResult.data ?? [];

    return occurrences.flatMap(occurrence => {
      const definition = definitions.get(occurrence.task_definition_id);
      if (!definition) return [];

      return [{
        occurrenceId: occurrence.id,
        definitionId: definition.id,
        title: definition.title,
        description: definition.description,
        priority: definition.priority,
        isDefinitionActive: definition.is_active,
        isDefinitionArchived: definition.archived_at !== null,
        date: occurrence.scheduled_for,
        status: this.mapStatus(occurrence.status, occurrence.cancellation_reason),
        measureType: occurrence.routine_measure_type_snapshot ?? definition.routine_measure_type ?? null,
        measureUnit: occurrence.routine_measure_unit_snapshot ?? definition.routine_measure_unit ?? null,
        defaultQuantity: occurrence.routine_default_quantity_snapshot === null || occurrence.routine_default_quantity_snapshot === undefined
          ? (definition.routine_default_quantity === null || definition.routine_default_quantity === undefined ? null : Number(definition.routine_default_quantity))
          : Number(occurrence.routine_default_quantity_snapshot),
        quantity: occurrence.routine_quantity === null || occurrence.routine_quantity === undefined ? null : Number(occurrence.routine_quantity),
        subtasks: this.mapSubtasks(occurrence.id, subtaskRows),
        recentDays: []
      } satisfies RoutineDayEntry];
    });
  }

  async getRoutineHistory(
    definitionIds: number[],
    startDate: string,
    endDate: string
  ): Promise<RoutineHistoryRecord[]> {
    if (!definitionIds.length) return [];

    const { data, error } = await this.client
      .from('task_occurrences')
      .select(`
        task_definition_id,
        scheduled_for,
        status,
        cancellation_reason
      `)
      .eq('task_kind', 'ROUTINE')
      .in('task_definition_id', definitionIds)
      .gte('scheduled_for', startDate)
      .lte('scheduled_for', endDate)
      .order('scheduled_for', { ascending: true });

    if (error) throw error;

    return (data ?? []).map(row => ({
      definitionId: row.task_definition_id,
      date: row.scheduled_for,
      status: this.mapStatus(row.status, row.cancellation_reason)
    }));
  }

  async getDefinitions(): Promise<RoutineDefinition[]> {
    const { data: definitions, error: definitionsError } = await this.client
      .from('task_definitions')
      .select(`
        id,
        title,
        description,
        priority,
        is_active,
        routine_active_from,
        routine_default_axis_system_key,
        routine_measure_type,
        routine_measure_unit,
        routine_default_quantity,
        created_at
      `)
      .eq('kind', 'ROUTINE')
      .is('archived_at', null)
      .order('created_at', { ascending: true });

    if (definitionsError) throw definitionsError;
    if (!definitions?.length) return [];

    const definitionIds = definitions.map(definition => definition.id);
    const { data: templates, error: templatesError } = await this.client
      .from('task_subtask_templates')
      .select(`
        task_definition_id,
        title,
        position
      `)
      .in('task_definition_id', definitionIds)
      .is('archived_at', null)
      .order('position', { ascending: true });

    if (templatesError) throw templatesError;

    return definitions.map(definition => ({
      definitionId: definition.id,
      title: definition.title,
      description: definition.description,
      priority: definition.priority,
      isActive: definition.is_active,
      activeFrom: definition.routine_active_from,
      defaultPeriod: definition.routine_default_axis_system_key,
      measureType: definition.routine_measure_type,
      measureUnit: definition.routine_measure_unit,
      defaultQuantity: definition.routine_default_quantity === null || definition.routine_default_quantity === undefined
        ? null
        : Number(definition.routine_default_quantity),
      templateSubtasks: (templates ?? [])
        .filter(template => template.task_definition_id === definition.id)
        .sort((a, b) => a.position - b.position)
        .map(template => template.title)
    }));
  }

  async createRoutine(input: RoutineInput): Promise<void> {
    const { error } = await this.client.rpc('create_routine', {
      p_title: input.title.trim(),
      p_description: input.description?.trim() || null,
      p_priority: input.priority,
      p_active_from: input.activeFrom,
      p_subtasks: this.cleanSubtasks(input.subtasks),
      p_default_axis_system_key: input.defaultPeriod,
      p_measure_type: input.measureType,
      p_measure_unit: input.measureUnit,
      p_default_quantity: input.defaultQuantity
    });
    if (error) throw error;
  }

  async updateRoutine(definitionId: number, input: RoutineInput): Promise<void> {
    const { error } = await this.client.rpc('update_routine', {
      p_definition_id: definitionId,
      p_title: input.title.trim(),
      p_description: input.description?.trim() || null,
      p_priority: input.priority,
      p_subtasks: this.cleanSubtasks(input.subtasks),
      p_default_axis_system_key: input.defaultPeriod,
      p_measure_type: input.measureType,
      p_measure_unit: input.measureUnit,
      p_default_quantity: input.defaultQuantity
    });
    if (error) throw error;
  }

  async setStatus(occurrenceId: number, status: RoutineStatus, quantity: number | null = null): Promise<void> {
    const { error } = await this.client.rpc('set_routine_occurrence_status', {
      p_occurrence_id: occurrenceId,
      p_status: status,
      p_quantity: quantity
    });
    if (error) throw error;
  }

  async setSubtaskCompleted(subtaskId: number, completed: boolean): Promise<void> {
    const { error } = await this.client.rpc('set_routine_subtask_completed', {
      p_subtask_id: subtaskId,
      p_completed: completed
    });
    if (error) throw error;
  }

  async deactivateRoutine(definitionId: number, effectiveOn: string): Promise<void> {
    const { error } = await this.client.rpc('deactivate_routine', {
      p_definition_id: definitionId,
      p_effective_on: effectiveOn
    });
    if (error) throw error;
  }

  async reactivateRoutine(definitionId: number, activeFrom: string): Promise<void> {
    const { error } = await this.client.rpc('reactivate_routine', {
      p_definition_id: definitionId,
      p_active_from: activeFrom
    });
    if (error) throw error;
  }

  async archiveRoutine(definitionId: number, effectiveOn: string): Promise<void> {
    const { error } = await this.client.rpc('archive_routine', {
      p_definition_id: definitionId,
      p_effective_on: effectiveOn
    });
    if (error) throw error;
  }

  private mapStatus(status: RoutineOccurrenceStatus, cancellationReason: string | null): RoutineStatus {
    if (status === 'COMPLETED') return 'COMPLETED';
    if (status === 'CANCELLED') {
      return cancellationReason === 'ROUTINE_CANCELLED' ? 'CANCELLED' : 'NOT_COMPLETED';
    }
    return 'NOT_RECORDED';
  }

  private mapSubtasks(occurrenceId: number, rows: RoutineSubtaskRow[]): RoutineSubtask[] {
    return rows
      .filter(row => row.occurrence_id === occurrenceId)
      .sort((a, b) => a.position - b.position)
      .map(row => ({
        id: row.id,
        title: row.title_snapshot,
        position: row.position,
        isCompleted: row.is_completed,
        completedAt: row.completed_at
      }));
  }

  private cleanSubtasks(subtasks: string[]): string[] {
    return subtasks.map(title => title.trim()).filter(Boolean);
  }
}
